class VidarPurchaseStatus extends HTMLElement {
  constructor() {
    super();

    this.onVariantChange = this.onVariantChange.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onPickerMutation = this.onPickerMutation.bind(this);
    this.syncFromForm = this.syncFromForm.bind(this);
  }

  connectedCallback() {
    this.formId = this.getAttribute("form");
    this.productInfo = this.closest(".product-info");
    this.inStockState = this.querySelector('[data-vidar-state="in-stock"]');
    this.restockState = this.querySelector('[data-vidar-state="restock"]');
    this.preorderState = this.querySelector('[data-vidar-state="preorder"]');
    this.showInStock = this.hasAttribute("show-in-stock");
    this.variantStatuses = this.parseVariantStatuses();
    this.currentVariant = this.getVariantStatus(this.getSelectedVariantId());

    // Impact bubbles variant:change from the product form. Listening on the
    // document also covers Shopify Theme Editor section reloads where this
    // component can connect before the product form has been inserted.
    document.addEventListener("variant:change", this.onVariantChange);
    document.addEventListener("change", this.onInputChange);

    const variantPicker = this.productInfo?.querySelector(`variant-picker[form="${CSS.escape(this.formId)}"]`);

    if (variantPicker) {
      this.pickerObserver = new MutationObserver(this.onPickerMutation);
      this.pickerObserver.observe(variantPicker, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      });
    }

    this.update(this.currentVariant);
    this.scheduleSync();
  }

  disconnectedCallback() {
    document.removeEventListener("variant:change", this.onVariantChange);
    document.removeEventListener("change", this.onInputChange);
    this.pickerObserver?.disconnect();
  }

  getProductForm() {
    return document.getElementById(this.formId);
  }

  parseVariantStatuses() {
    const statusData = this.querySelector("[data-vidar-variant-statuses]");

    if (!statusData) {
      return new Map();
    }

    try {
      const statuses = JSON.parse(statusData.textContent);
      return new Map(statuses.map((status) => [Number(status.id), status]));
    } catch (error) {
      console.warn("VIDAR purchase status could not parse variant data.", error);
      return new Map();
    }
  }

  getSelectedVariantId() {
    const variantInput = this.getProductForm()?.elements.namedItem("id");
    return variantInput ? Number(variantInput.value) : Number(this.getAttribute("selected-variant-id"));
  }

  getVariantStatus(variantId) {
    return this.variantStatuses.get(Number(variantId)) || null;
  }

  onVariantChange(event) {
    const variantId = Number(event.detail?.variant?.id);

    // Impact can dispatch the event from different elements depending on how
    // the product was opened or re-rendered in the Theme Editor. Variant IDs
    // are unique, so the component's own status map is the reliable boundary.
    if (!this.variantStatuses.has(variantId)) {
      return;
    }

    this.currentVariant = this.getVariantStatus(variantId);
    this.update(this.currentVariant);
  }

  onInputChange(event) {
    const isMasterSelector =
      event.target?.name === "id" && event.target?.form?.id === this.formId;
    const variantPicker = event.target?.closest?.("variant-picker");

    if (!isMasterSelector && variantPicker?.getAttribute("form") !== this.formId) {
      return;
    }

    this.scheduleSync();
  }

  onPickerMutation() {
    this.scheduleSync();
  }

  scheduleSync() {
    if (this.syncScheduled) {
      return;
    }

    this.syncScheduled = true;

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this.syncScheduled = false;

        if (this.isConnected) {
          this.syncFromForm();
        }
      });
    });
  }

  syncFromForm() {
    const variant = this.getVariantStatus(this.getSelectedVariantId());

    if (!variant) {
      return;
    }

    this.currentVariant = variant;
    this.update(variant);
  }

  update(variant) {
    const hasVariant = Boolean(variant);
    const isPreorder = hasVariant && Boolean(variant?.preorder);
    const isInStock = this.isInStockVariant(variant);
    const isSoldOut = hasVariant && !variant.available && !isPreorder;

    if (this.inStockState) {
      this.inStockState.hidden = !(this.showInStock && isInStock);
    }

    if (this.restockState) {
      this.restockState.hidden = !isSoldOut;
    }

    if (this.preorderState) {
      this.preorderState.hidden = !isPreorder;
    }

    this.toggleQuantitySelector(isSoldOut);
    this.toggleFixedAddToCart(isSoldOut);
    this.updatePreorderProperties(variant, isPreorder);
    this.updateDiscoveryHint(isInStock);
    this.updatePreorderOptionBadges(variant);
    this.updatePreorderButtons(isPreorder);
  }

  isInStockVariant(variant) {
    return Boolean(variant?.available) && !variant?.preorder;
  }

  toggleQuantitySelector(isSoldOut) {
    const quantitySelector = this.productInfo?.querySelector(".product-info__quantity-selector");

    if (quantitySelector) {
      quantitySelector.hidden = isSoldOut;
    }
  }

  toggleFixedAddToCart(isSoldOut) {
    const fixedAddToCart = document.querySelector(`product-quick-add[form="${CSS.escape(this.formId)}"]`);

    if (fixedAddToCart) {
      fixedAddToCart.hidden = isSoldOut;
    }
  }

  updatePreorderProperties(variant, isPreorder) {
    const productForm = this.getProductForm();

    if (!productForm) {
      return;
    }

    const inputs = productForm.querySelectorAll("[data-vidar-preorder-property]");

    for (const input of inputs) {
      input.disabled = !isPreorder;

      if (!isPreorder) {
        continue;
      }

      switch (input.dataset.vidarPreorderProperty) {
        case "status":
          input.value = "true";
          break;
        case "date":
          input.value = variant?.shippingDate || "";
          break;
        case "notice":
          input.value = variant?.shippingDateLabel
            ? `Vorbestellung – voraussichtlicher Versand ab ${variant.shippingDateLabel}`
            : "Vorbestellung – voraussichtlicher Versandtermin folgt";
          break;
      }
    }
  }

  updatePreorderButtons(isPreorder) {
    if (!isPreorder) {
      return;
    }

    queueMicrotask(() => {
      const buyButtons = document.querySelectorAll(
        `buy-buttons[form="${CSS.escape(this.formId)}"] button[type="submit"]`
      );

      for (const button of buyButtons) {
        const label = button.getAttribute("is") === "custom-button" ? button.firstElementChild : button;

        if (label) {
          label.textContent = "JETZT VORBESTELLEN";
        }
      }
    });
  }

  updateDiscoveryHint(isAvailable) {
    const discoveryHint = this.productInfo?.querySelector(
      `[data-vidar-restock-discovery][data-form="${CSS.escape(this.formId)}"]`
    );

    if (!discoveryHint) {
      return;
    }

    const previousElement = discoveryHint.previousElementSibling;
    const sizeFieldset = previousElement?.matches(".variant-picker__option")
      ? previousElement
      : discoveryHint.closest("variant-picker")?.querySelector(".variant-picker__option");
    const hasSoldOutSize = Boolean(
      sizeFieldset?.querySelector("[data-option-value].is-disabled:not([hidden])")
    );

    discoveryHint.hidden = !isAvailable || !hasSoldOutSize;
  }

  updatePreorderOptionBadges(selectedVariant) {
    const sizeFieldsets = this.productInfo?.querySelectorAll("[data-vidar-size-option-position]") || [];
    const selectedOptions = selectedVariant?.options || [];

    for (const fieldset of sizeFieldsets) {
      const optionIndex = Number(fieldset.dataset.vidarSizeOptionPosition) - 1;

      if (optionIndex < 0) {
        continue;
      }

      for (const optionElement of fieldset.querySelectorAll("[data-option-value]")) {
        const optionValue = optionElement.control?.value || optionElement.getAttribute("value");
        const matchingVariant = Array.from(this.variantStatuses.values()).find((variant) => {
          if (!Array.isArray(variant.options) || variant.options[optionIndex] !== optionValue) {
            return false;
          }

          return variant.options.every((value, index) => {
            return index === optionIndex || value === selectedOptions[index];
          });
        });
        const isPreorder = Boolean(matchingVariant?.preorder);

        optionElement.toggleAttribute("data-vidar-preorder-option", isPreorder);

        if (isPreorder) {
          optionElement.setAttribute("title", "Vorbestellung – längere Lieferzeit");
          optionElement.setAttribute("aria-label", `${optionValue} – Vorbestellung mit längerer Lieferzeit`);
        } else {
          optionElement.removeAttribute("title");
          optionElement.removeAttribute("aria-label");
        }
      }
    }
  }
}

if (!window.customElements.get("vidar-purchase-status")) {
  window.customElements.define("vidar-purchase-status", VidarPurchaseStatus);
}
